import {useEffect, type RefObject} from 'react'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {Spinner} from '@primer/react'
import {useAgentModels} from '@github-ui/copilot-shared-ui/agents/use-agent-models'
import {agentSupportsModelSelection} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {ModelPicker as AgentModelPicker} from '@github-ui/one-copilot-composer/ModelPicker'
import {ReasoningEffortPicker} from '@github-ui/one-copilot-composer/ReasoningEffortPicker'
import {useAgentVendorModel} from '@github-ui/one-copilot-composer/use-agent-vendor-model'
import {useLastSelectedAgent} from '@github-ui/one-copilot-composer/use-last-selected-agent'
import {agentModelScopeKey} from '@github-ui/one-copilot-composer/use-latest-agent-model'
import {useLatestReasoningEffort} from '@github-ui/one-copilot-composer/use-latest-reasoning-effort'
import {
  useManagedDefaultModel,
  useModelSelectionLoadingAnnouncement,
} from '@github-ui/one-copilot-composer/use-managed-default-model'
import {
  useModelSupportedReasoningEfforts,
  useReasoningEffortAutoFallback,
  useSelectModelClearingReasoningEffort,
} from '@github-ui/one-copilot-composer/use-model-supported-reasoning-efforts'
import type {SelectedAgent} from '@github-ui/agent-sessions-types/agent-identity'
import type {CustomModelPickerProps, CustomModelSelectionState} from '@github-ui/copilot-chat/plugin'

interface SlashCommandModelPickerProps extends Pick<
  CustomModelPickerProps,
  'announcementElementRef' | 'onModelSelectionChange' | 'isAhpEnvironment'
> {
  /**
   * The currently selected agent, passed from the parent ChatInput context.
   * This ensures the model picker updates reactively when the agent selection changes.
   */
  selectedAgent?: SelectedAgent
  managedDefaultModelEnabled?: boolean
}

const emptyAnnouncementElementRef: RefObject<HTMLElement | null> = {current: null}

/**
 * Model picker component for the /task slash command.
 * This wraps the base ModelPicker with scoped managed-default selection and reports
 * the effective managed-session model to ChatInput for explicit task submission.
 */
export function SlashCommandModelPicker({
  selectedAgent: selectedAgentProp,
  managedDefaultModelEnabled = false,
  announcementElementRef,
  onModelSelectionChange,
  isAhpEnvironment = false,
}: SlashCommandModelPickerProps) {
  const {tokenBasedBillingEnabled} = useEntitlement()
  const {
    models: agentModels,
    defaultModelId: managedDefaultModelId,
    isLoading: isLoadingModels,
    error: modelsError,
  } = useAgentModels()
  const [selectedAgentFromStorage] = useLastSelectedAgent()
  const selectedAgent = selectedAgentProp ?? selectedAgentFromStorage

  const {
    agentSlug,
    vendorFilters,
    selectedModelId: persistedModelId,
    setSelectedModelId: setSelectedAgentModelId,
    isVendorScopeLoading,
  } = useAgentVendorModel(selectedAgent)
  const [selectedReasoningEffort, setSelectedReasoningEffort] = useLatestReasoningEffort()
  const selectModelBase = useSelectModelClearingReasoningEffort(
    setSelectedAgentModelId,
    selectedReasoningEffort,
    setSelectedReasoningEffort,
  )
  const {selectedModelId, selectModel, isSelectionReady, isUsingManagedDefault} = useManagedDefaultModel(
    managedDefaultModelEnabled ? managedDefaultModelId : undefined,
    managedDefaultModelEnabled && (isLoadingModels || isVendorScopeLoading),
    agentModelScopeKey(vendorFilters),
    persistedModelId,
    selectModelBase,
  )
  const supportedReasoningEfforts = useModelSupportedReasoningEfforts(selectedModelId)
  const selectedReasoningEffortForSession =
    !isSelectionReady || isUsingManagedDefault ? undefined : selectedReasoningEffort
  useReasoningEffortAutoFallback(
    selectedReasoningEffortForSession,
    supportedReasoningEfforts,
    setSelectedReasoningEffort,
  )

  // AHP prompts carry the model explicitly, so report the selection there even when the
  // managed default isn't in play (the cloud path otherwise lets the backend choose).
  const reportsSelection =
    isSelectionReady && (isAhpEnvironment || (managedDefaultModelEnabled && !!managedDefaultModelId))

  useEffect(() => {
    const selection: CustomModelSelectionState = {
      isReady: isSelectionReady,
      ...(reportsSelection
        ? {
            modelId: selectedModelId,
            reasoningEffort: isUsingManagedDefault ? null : selectedReasoningEffortForSession,
          }
        : {}),
    }
    onModelSelectionChange?.(selection)
  }, [
    isSelectionReady,
    isUsingManagedDefault,
    onModelSelectionChange,
    reportsSelection,
    selectedModelId,
    selectedReasoningEffortForSession,
  ])

  useEffect(() => () => onModelSelectionChange?.({isReady: false}), [onModelSelectionChange])

  useModelSelectionLoadingAnnouncement(
    false,
    isSelectionReady,
    modelsError,
    announcementElementRef ?? emptyAnnouncementElementRef,
  )

  // The agent selector is hidden for AHP environments, so keep the model picker visible
  // there even when the persisted agent selection would otherwise suppress it.
  const shouldShowModelPicker = isAhpEnvironment || agentSupportsModelSelection(selectedAgent, agentSlug)

  if (!isSelectionReady) {
    return <Spinner size="small" srText="Loading models…" />
  }

  if (!shouldShowModelPicker || !agentModels || agentModels.length === 0) {
    return null
  }

  return (
    <>
      <AgentModelPicker
        onUpdateModel={selectModel}
        selectedModelId={selectedModelId}
        vendorFilters={vendorFilters}
        isTokenBasedBillingEnabled={tokenBasedBillingEnabled}
      />
      {copilotFeatureFlags.codingAgentReasoningEffort && !isUsingManagedDefault && (
        <ReasoningEffortPicker
          selectedReasoningEffort={selectedReasoningEffortForSession}
          onUpdateReasoningEffort={setSelectedReasoningEffort}
          supportedReasoningEfforts={supportedReasoningEfforts}
        />
      )}
    </>
  )
}
